import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import AppraisalFooter from '../../../../components/custom_components/Appraisal/AppraisalFooter'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { useTargetsModalStore } from '../../../../components/custom_components/TargetsModal/TargetsModalStore'
import useManagerMeetingStepTargets from '../../../../graphql/appraisal-flow/hooks/useManagerMeetingStepTargets'
import {
  BonusBetweenAnnualBonusAndProrataDto,
  GetManagerMeetingStepTargetsQuery,
  MeetingStepStatus,
  MeetingStepViewId,
  MeetingType,
} from '../../../../types'
import { useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId } from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import { getFlexibleBonusUnknownCurrencyForConversion, toEligibleFlexibleBonusOverride } from '../shared/flexibleBonusUtil'
import { retrieveContractStartAndEndDateFromContract } from '../../../profile/contract/utils/contractUtil'
import { ClosedTargetsComponent } from '../../okr/closedTargets/ClosedTargetsComponent'
import { ManagerExistingObjectives } from '../../okr/ManagerExistingObjectives'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import AppraisalPageDescription from '../shared/AppraisalPageDescription'
import { PastTargetsFooter } from '../shared/PastTargetsFooter'
import { calculatePastTargetsData } from '../shared/util'
import { validatePastTargetsData } from './util/pastTargetsUtil'
import { usePastTargetsWithPaymentsValidator } from './util/usePastTargetsWithPaymentsValidator'
import { useExistingDevTargetsToBeClosed } from '../../../../graphql/objectives/useExistingDevTargetsToBeClosed'
import { useCurrency } from '../../../../context/currency-context'
import { OutdatedTargetsComponent } from '../../okr/oldActiveTargets/OutdatedTargetsComponent'
import { Divider } from 'antd'

interface AppraisalMeetingStepPastTargetsContentProps {
  data: GetManagerMeetingStepTargetsQuery
  existingDevTargetsToBeClosed: boolean
  pastYearFlexibleBonus?: BonusBetweenAnnualBonusAndProrataDto | null
}

export const AppraisalMeetingStepPastTargets: FC<MeetingStepDefaultProps> = ({ meeting, backBtn, nextBtn }) => {
  const { data, loading, error } = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.pastTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
    true,
  )

  const {
    data: existingDevTargetsData,
    loading: existingDevTargetsLoading,
    error: existingDevTargetsError,
  } = useExistingDevTargetsToBeClosed(meeting.id)

  const isAppraisal = meeting.type === MeetingType.Appraisal
  const {
    data: pastYearFlexibleBonusData,
    loading: pastYearFlexibleBonusLoading,
    error: pastYearFlexibleBonusError,
  } = useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id, !isAppraisal)

  if (
    loading ||
    error ||
    !data ||
    existingDevTargetsError ||
    existingDevTargetsLoading ||
    !existingDevTargetsData ||
    pastYearFlexibleBonusError ||
    (isAppraisal && pastYearFlexibleBonusLoading)
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || existingDevTargetsLoading || pastYearFlexibleBonusLoading}
        error={error || existingDevTargetsError || pastYearFlexibleBonusError}
        showSpinner
      />
    )
  } else {
    return (
      <AppraisalMeetingStepPastTargetsContent
        data={data}
        meeting={meeting}
        backBtn={backBtn}
        nextBtn={nextBtn}
        existingDevTargetsToBeClosed={existingDevTargetsData.existingDevObjectivesToBeClosedInMeeting}
        pastYearFlexibleBonus={pastYearFlexibleBonusData?.findPastYearFlexibleBonusByEmployeeIdAndMeetingId}
      />
    )
  }
}

const AppraisalMeetingStepPastTargetsContent: FC<MeetingStepDefaultProps & AppraisalMeetingStepPastTargetsContentProps> = ({
  data,
  meeting,
  existingDevTargetsToBeClosed,
  pastYearFlexibleBonus,
  backBtn,
  nextBtn,
}) => {
  const [t] = useTranslation()
  const [setModalProps, cleanModalProps] = useTargetsModalStore((state) => [state.setModalProps, state.cleanModalProps], shallow)
  const { currencyConversionData } = useCurrency()

  usePastTargetsWithPaymentsValidator()

  const isAppraisalUpdate = meeting.type === MeetingType.AppraisalUpdate
  const { pastTargetsReferenceYear } = meeting.targetsCalculationHelper
  const {
    objectivesInMeeting,
    targetBonusOfYear,
    currentContract,
    salaryOfYear,
    branchOfficeByEmployee,
    isBonusValidationEnabled,
    contractChangesByEmployee,
    contractsInYear,
  } = data

  const pastYearFlexibleBonusOverride = toEligibleFlexibleBonusOverride(
    pastYearFlexibleBonus,
    currentContract!.currencyForPayments,
    currencyConversionData,
    branchOfficeByEmployee.timezone,
    pastTargetsReferenceYear,
    contractsInYear,
    targetBonusOfYear,
    contractChangesByEmployee,
  )
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    pastYearFlexibleBonus,
    currentContract!.currencyForPayments,
    currencyConversionData,
  )

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(currentContract)
  const {
    cannotProceed,
    isMonetaryTargetMissing,
    tooMuchTargetBonusProvided,
    allPastMonetaryObjectivesChecked,
    existingDevObjectivesToBeClosed,
    unknownCurrenciesForConversion,
  } = validatePastTargetsData(
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    objectivesInMeeting,
    existingDevTargetsToBeClosed,
    targetBonusOfYear,
    salaryOfYear,
    currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0,
    currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0,
    pastTargetsReferenceYear,
    contractChangesByEmployee,
    currencyConversionData,
    currentContract!.currencyForPayments,
    contractStartDate,
    contractEndDate,
    contractsInYear,
    pastYearFlexibleBonusOverride,
    flexibleBonusUnknownCurrency,
  )

  const { openTargetBonus, targetBonusAmount, outdatedTargets } = calculatePastTargetsData(
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    targetBonusOfYear,
    salaryOfYear,
    currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0,
    currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0,
    contractChangesByEmployee,
    objectivesInMeeting,
    currencyConversionData,
    currentContract!.currencyForPayments,
    pastTargetsReferenceYear,
    contractStartDate,
    contractEndDate,
    contractsInYear,
    pastYearFlexibleBonusOverride,
    flexibleBonusUnknownCurrency,
  )

  const existingDevObjectivesToBeClosedText = t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingDevObjectivesToBeClosed')
  const textMonetaryTargetMissing = t('appraisalMeeting.stepTargetAndBonus.disabledButtonLabel.missingTargets')
  const textAchievedNotChecked = t('appraisalMeeting.stepTargetAndBonus.disabledButtonLabel.notAllAchievedChecked')
  const textTooMuchTargetBonus = t('appraisalMeeting.stepTargetAndBonus.disabledButtonLabel.textTooMuchTargetBonus')
  const existingOutdatedTargetsText = t('appraisalMeeting.stepTargetAndBonus.disabledButtonLabel.existingOutdatedTargets')
  const tooltipText = !!unknownCurrenciesForConversion.length
    ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.unknownConversionRate', {
        currentContractCurrency: currentContract!.currencyForPayments,
        unknownCurrencies: unknownCurrenciesForConversion.join(', '),
      })
    : isMonetaryTargetMissing
    ? textMonetaryTargetMissing
    : tooMuchTargetBonusProvided
    ? textTooMuchTargetBonus
    : !allPastMonetaryObjectivesChecked
    ? textAchievedNotChecked
    : existingDevObjectivesToBeClosed
    ? existingDevObjectivesToBeClosedText
    : !!outdatedTargets.length
    ? existingOutdatedTargetsText
    : ''

  const currentStepStatus = cannotProceed ? MeetingStepStatus.Incomplete : MeetingStepStatus.Done

  useEffect(() => {
    setModalProps({
      employeeId: meeting.employee.id,
      meetingId: meeting.id,
      meeting,
      isAppraisalUpdate,
      targetBonus: targetBonusAmount,
      alreadyPlanned: openTargetBonus,
      employeeCurrency: currentContract ? currentContract.currencyForPayments : null,
      employeeCountry: meeting.employee.currentContract ? meeting.employee.currentContract.legalEntity.country : null,
      allowedPastTargets: meeting.allowedMonetaryTargets.pastTargetsAllowed && openTargetBonus > 0,
      disabledMonetaryButtonTooltip:
        openTargetBonus <= 0
          ? t('appraisalMeeting.stepTargetAndBonus.disabledMonetaryButton.targetBonusAlreadyDistributed')
          : !meeting.allowedMonetaryTargets.pastTargetsAllowed
          ? t('appraisalMeeting.stepTargetAndBonus.disabledMonetaryButton.targetsNotAllowedForLowEmployee')
          : '',
    })
    return () => {
      cleanModalProps()
    }
  }, [isAppraisalUpdate, setModalProps, targetBonusAmount, openTargetBonus, cleanModalProps, currentContract, meeting, t])

  return (
    <div data-e2e-appraisal-step-past-targets>
      <MeetingBackground withFooter>
        <AppraisalPageDescription description={t('appraisalMeeting.stepTargetAndBonus.pageDescriptionMeeting')} />
        {!!outdatedTargets.length && (
          <>
            <OutdatedTargetsComponent outdatedTargets={outdatedTargets} employee={meeting.employee} meetingId={meeting.id} />
            <Divider />
          </>
        )}

        <ManagerExistingObjectives
          meeting={meeting}
          objectives={objectivesInMeeting}
          targetBonusOfYear={targetBonusOfYear}
          salaryOfYear={salaryOfYear}
          currentSalary={currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0}
          currentBonus={currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          employeeTimezone={branchOfficeByEmployee.timezone}
          isBonusValidationEnabled={isBonusValidationEnabled}
          contractChangesByEmployee={contractChangesByEmployee}
          contractsInYear={contractsInYear}
          pastYearFlexibleBonusOverride={pastYearFlexibleBonusOverride}
          flexibleBonusUnknownCurrency={flexibleBonusUnknownCurrency}
        />

        <ClosedTargetsComponent objectives={objectivesInMeeting} />
      </MeetingBackground>
      <AppraisalFooter>
        <PastTargetsFooter
          objectives={objectivesInMeeting}
          targetBonusOfYear={targetBonusOfYear}
          salaryOfYear={salaryOfYear}
          currentSalary={currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0}
          currentBonus={currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0}
          referenceYear={pastTargetsReferenceYear}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          employeeTimezone={branchOfficeByEmployee.timezone}
          isBonusValidationEnabled={isBonusValidationEnabled}
          contractChangesByEmployee={contractChangesByEmployee}
          contractsInYear={contractsInYear}
          pastYearFlexibleBonusOverride={pastYearFlexibleBonusOverride}
          flexibleBonusUnknownCurrency={flexibleBonusUnknownCurrency}
        />
      </AppraisalFooter>

      <PerFlexRow justify="space-between">
        {backBtn && backBtn(currentStepStatus)}
        {nextBtn && nextBtn(currentStepStatus, false, tooltipText)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.PastTargets} stepStatus={currentStepStatus} />
    </div>
  )
}
